import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    OnInit,
    Renderer2,
    computed,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';
import { TranslateService } from '@ngx-translate/core';

type SidebarSide = 'left' | 'right';

@Component({
    selector: 'app-three-pane-layout',
    imports: [AppIcon],
    templateUrl: './three-pane-layout.html',
    styleUrl: './three-pane-layout.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThreePaneLayout implements OnInit {
    private readonly translate = inject(TranslateService);

    readonly showLeftSidebar = input(true);
    readonly showRightSidebar = input(true);
    readonly initialSidebarSize = input(250);
    readonly initialRightSidebarSize = input<number | null>(null);
    readonly minSidebarSize = input(250);
    readonly maxSidebarSize = input(500);
    readonly rightSidebarForcedSize = input<number | null>(null);
    readonly showRightSidebarToggle = input(true);
    readonly showRightSidebarResize = input(true);
    readonly rightSidebarOpen = input<boolean | null>(null);
    readonly leftSidebarLabel = input(this.translate.instant('shared.layout.leftSidebar'));
    readonly rightSidebarLabel = input(this.translate.instant('shared.layout.rightSidebar'));
    readonly leftToggleLabel = input(this.translate.instant('shared.layout.toggleLeftSidebar'));
    readonly rightToggleLabel = input(this.translate.instant('shared.layout.toggleRightSidebar'));

    protected readonly leftSidebarIcon = 'icon-sidebar-left-regular.svg';
    protected readonly rightSidebarIcon = 'icon-sidebar-right-regular.svg';
    protected readonly leftSidebarSolidIcon = 'icon-sidebar-left-solid.svg';
    protected readonly rightSidebarSolidIcon = 'icon-sidebar-right-solid.svg';

    protected readonly isLeftOpen = signal(true);
    protected readonly isRightOpen = signal(true);
    protected readonly leftSize = signal<number | null>(null);
    protected readonly rightSize = signal<number | null>(null);
    protected readonly leftSidebarSize = computed(() =>
        this.clampSize(this.leftSize() ?? this.initialSidebarSize()),
    );
    protected readonly rightSidebarSize = computed(() => {
        const forcedSize = this.rightSidebarForcedSize();
        if (forcedSize != null) {
            return this.clampSize(forcedSize);
        }

        return this.clampSize(
            this.rightSize() ?? this.initialRightSidebarSize() ?? this.initialSidebarSize(),
        );
    });

    private readonly destroyRef = inject(DestroyRef);
    private readonly renderer = inject(Renderer2);
    private stopPointerMove: (() => void) | null = null;
    private stopPointerUp: (() => void) | null = null;

    ngOnInit(): void {
        this.isLeftOpen.set(!this.isMobile());
        this.isRightOpen.set(!this.isMobile());
    }

    protected toggleLeft(): void {
        if (this.isMobile() && !this.isLeftOpen()) {
            this.isRightOpen.set(false);
        }
        this.isLeftOpen.update((isOpen) => !isOpen);
    }

    protected toggleRight(): void {
        if (this.isMobile() && !this.isRightOpen()) {
            this.isLeftOpen.set(false);
        }
        this.isRightOpen.update((isOpen) => !isOpen);
    }

    protected closeSidebars(): void {
        this.isLeftOpen.set(false);
        this.isRightOpen.set(false);
    }

    protected startResize(event: PointerEvent, side: SidebarSide): void {
        event.preventDefault();

        const startX = event.clientX;
        const startSize = side === 'left' ? this.leftSidebarSize() : this.rightSidebarSize();

        this.stopResizeListeners();
        this.stopPointerMove = this.renderer.listen(
            'document',
            'pointermove',
            (moveEvent: PointerEvent) => {
                const delta = moveEvent.clientX - startX;
                const nextSize = side === 'left' ? startSize + delta : startSize - delta;
                this.setSidebarSize(side, nextSize);
            },
        );
        this.stopPointerUp = this.renderer.listen('document', 'pointerup', () =>
            this.stopResizeListeners(),
        );
    }

    private setSidebarSize(side: SidebarSide, size: number): void {
        if (side === 'left') {
            this.leftSize.set(this.clampSize(size));
            return;
        }

        this.rightSize.set(this.clampSize(size));
    }

    private clampSize(size: number): number {
        const minSize = Math.min(this.minSidebarSize(), this.maxSidebarSize());
        const maxSize = Math.max(this.initialSidebarSize(), this.maxSidebarSize());

        return Math.min(Math.max(size, minSize), maxSize);
    }

    private stopResizeListeners(): void {
        this.stopPointerMove?.();
        this.stopPointerUp?.();
        this.stopPointerMove = null;
        this.stopPointerUp = null;
    }

    constructor() {
        effect(() => {
            const requestedState = this.rightSidebarOpen();
            if (requestedState != null) this.isRightOpen.set(requestedState);
        });
        this.destroyRef.onDestroy(() => this.stopResizeListeners());
    }

    protected isMobile(): boolean {
        return window.innerWidth <= 48 * 16; // 48rem in pixels
    }
}
